import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  changes,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * 낌새 개인정보 처리방침.
 *
 * **다른 방침을 그대로 베끼면 이 서비스에서는 거짓이 된다.** 또렷·시험보소 같은 앱은
 * "생년월일·성별은 어떤 경로로도 요구하지 않는다"고 적어 두었는데, 낌새는 **정확히 그
 * 둘을 필수로 받는다** — 그것이 사주의 입력이기 때문이다. 문장을 옮겨 오지 말 것.
 *
 * 조심할 자리 셋:
 *
 * 1. **생년월일시와 성별은 필수다.** 성별은 대운의 순행·역행을 가르는 값이라 뺄 수 없다.
 *    법상 민감정보(제23조)나 고유식별정보(제24조)에는 해당하지 않지만, 사람에 따라 매우
 *    사적으로 느끼는 값이라 무엇에 쓰는지까지 적는다.
 * 2. **대화 내용과 원국이 모델 제공자까지 간다.** OpenRouter 는 라우터라 두 단계를 다 적는다.
 *    그리고 **이름을 적었다면 그 이름도 함께 간다** — 모델이 "OO 님"으로 부르게 하려고
 *    시스템 프롬프트에 싣기 때문이다(`server/kkimsae` 의 `Interpreter.systemPrompt`).
 *    이름을 안 적으면 안 간다. 그 사실을 제2조·제6조에 그대로 적었다.
 * 3. **질문에 무엇을 쓸지는 사용자가 정한다.** 건강·가족·돈 같은 이야기를 스스로 적으면
 *    그것도 모델에 간다. "우리가 수집하지 않는다"로 끝내면 실제로 글이 닿는 곳을 감춘다.
 *
 * **광고는 아직 없다.** 화면 아래에 자리는 비워 두었지만 광고 SDK 가 붙어 있지 않고
 * 광고 식별자도 수집하지 않는다(`frontend/kkimsae/app/src/ads/AdBanner.tsx`). 게재를
 * 시작하는 커밋에서 이 방침과 스토어 데이터 보안 설문을 함께 고친다.
 */

const EFFECTIVE_DATE = 'August 28, 2026'
const LAST_REVISED = 'August 29, 2026'
const CRASH_RETENTION = '90 days'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Identifying members and keeping them signed in — identifying you by a social account, so that your reading and your conversations belong to an account rather than to a device',
      'Working out the chart the service is built on — taking the birth date, birth time, calendar, and sex you enter and deriving from them the four pillars used for every answer',
      'Answering what you ask — sending that chart, your question, and the recent turns of the conversation to a language-model processor so that an answer can be written',
      'Keeping your conversations — storing the questions you asked and the answers you received, so they survive a change of device',
      'Counting free answers and checking the subscription — keeping a ledger of the free answers you have used, and asking the payment processor whether your subscription is active',
      'Keeping the service reliable — finding and fixing the places where the app errors',
      SUPPORT_PURPOSE,
    ],
    note: 'No automated decision is made that has a legal effect on you or a comparably significant impact. What the service returns is an interpretation offered for entertainment; it is not advice on your health, your legal position, or your finances, it is shown to no one else, and it is never sent to any employer, insurer, lender, or other third party.',
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: [
      'The items below are everything the Operator processes. **The first group is the input the service exists to read** — without it there is nothing to answer.',
    ],
    bullets: [
      '[Required] Date of birth — the year, month, and day you enter, together with whether you entered them on the solar or the lunar calendar (and, for a lunar date, whether it fell in a leap month)',
      '[Required] Sex (male or female) — used for one purpose only: the direction in which the ten-year cycles are counted runs one way for men and the other for women, so a chart cannot be built without it. It is not used to target you, to segment you, or to change what you are shown anywhere else',
      '[Optional] Time of birth — the hour and minute, if you know them. **“I don’t know” is a real choice**; when you take it, the hour pillar is treated as unknown rather than guessed',
      '[Optional] Display name — only if you enter one, so that answers can address you by it. Leave it blank and nothing is stored or sent',
      '[Generated automatically] The chart — the compact text derived from the items above (the eight characters, the elements, the ten gods, the ten-year and annual cycles). It is derived on your own device or in your own browser and then stored',
      '[Required] Account identity — the account identifier and profile name issued by the social provider you sign in with',
      '[Required] Email address — used only for account verification and recovery. This follows whatever the social provider supplies',
      '[Optional] Your conversations — the questions you type and the answers written for you. **What you put in a question is up to you**; if you write about your health, your family, your money, or anything else personal, that text is stored and is sent to the processors in Articles 6 and 7 along with the question',
      '[Generated automatically] Answer ledger — how many free answers you have been granted and used, and when',
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier, app version and build, device model, operating system, error messages, stack traces, and crash traces',
      '[Collected automatically] Purchase record — where you subscribe, the anonymous customer identifier and store transaction record held by the payment processor',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
    ],
    note: 'If you use a social login, the Operator never receives that provider’s password. Phone number, address, contacts, photographs, location, and payment card details are not requested by any route, and **no advertising identifier is collected, because the service currently shows no advertising**. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected: a date of birth and a sex are neither, and the Operator asks for no resident registration number, no passport number, and no biometric data. The Operator does not ask you for information about your health, your beliefs, or your political views — but note the item above about what you may choose to write into a question yourself.',
  },
  {
    heading: 'Article 3. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      ['Account identity and email address', 'Until you ask to withdraw (delete your account) — destroyed on request', 'Your consent'],
      [
        'Birth date, birth time, calendar, sex, display name, and the chart derived from them',
        'Until you change them or withdraw (destroyed immediately on withdrawal)',
        'Your consent',
      ],
      ['Conversations (your questions and the answers)', 'Until you delete the conversation or withdraw (destroyed immediately on withdrawal)', 'Your consent'],
      ['Answer ledger', 'Until you ask to withdraw — destroyed on request', 'Your consent'],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      ['Purchase record', 'Until the payment project is closed or deletion is requested', 'Your consent'],
      [...SUPPORT_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. ${SUPPORT_NOTE}`,
  },
  {
    heading: 'Article 4. Where the chart is worked out, and what leaves your device',
    paragraphs: [
      '**The chart is calculated on your own device**, or in your own browser on the website, by an open-source library that ships inside the app. Nothing is sent anywhere in order to work it out, and the website page that shows you a chart makes no request to the Operator’s server at all — you can read your own eight characters there without an account and without anything being stored.',
      'What is sent to the Operator’s server, once you sign in and ask something, is: the compact chart text, the question you typed, and the recent turns of that conversation. Your birth date, birth time, calendar, sex, and display name are stored on the server so that the chart can be rebuilt when you change device.',
      'What is sent onward to the language-model processor is narrower: **the chart, your question, the recent turns, and — only if you entered one — your display name**, so that the answer can address you. Your email address, your account identifier, and your purchase record are never sent there.',
      'The instruction given to the model forbids it from diagnosing or treating illness, from predicting the outcome of a lawsuit, from advising on a particular stock or property, and from naming a time of death; where you ask about any of those, it is told to describe only what the chart shows about disposition and timing and to point you to a professional.',
    ],
    note: 'Should this design change — should charts be calculated on the server, or conversations be used to improve any model — this policy and the store data safety declaration will be updated together and notice given before that change goes live. The processors named in Article 6 are instructed to process what they receive only in order to answer that one request; the Operator does not consent to your data being used to train their models and has selected processors on that basis.',
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold. Your chart and your conversations are shown to no other user, and there is no sharing, comparison, matching, or ranking between users.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      ['Kakao Corp.', 'Authenticating a Kakao account sign-in', 'Account identifier and profile name', 'Limited to the moment authentication is processed'],
      ['Google LLC', 'Authenticating a Google account sign-in', 'Account identifier, name, and email address', 'Limited to the moment authentication is processed'],
      ['Apple Inc.', 'Authenticating an Apple account sign-in', 'Account identifier and email address (which you may choose to hide)', 'Limited to the moment authentication is processed'],
      [
        'OpenRouter, Inc. and the model provider it routes the request to',
        'Writing the answer to what you asked',
        'The chart derived from your birth details, your question, the recent turns of that conversation, and your display name if you entered one. **No account identifier, email address, or purchase record is sent with them**',
        'Limited to the moment the request is answered',
      ],
      [...CRASHLYTICS_ROW],
      [...REVENUECAT_ROW],
    ]),
    note: 'OpenRouter, Inc. is a router: it passes each request to the model provider the Operator has selected and returns the answer. That provider is therefore a further recipient of what is listed above, and the Operator names the router and the provider together rather than the router alone. The Operator selects providers that do not retain requests for training and configures the request so that it is not retained; where the selected provider changes, this policy is updated and notice given in advance. In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Of the processors named in Article 6, all except Kakao Corp. are located outside Korea, and the entrustment transfers personal data abroad as follows. Kakao Corp. is a Korean company, so no transfer abroad is involved there.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [
        'OpenRouter, Inc. and the model provider it routes the request to',
        'The United States and, depending on the provider selected, other countries where that provider operates',
        'The chart derived from your birth details, your question, the recent turns of that conversation, and your display name if you entered one',
        'Sent over HTTPS at the moment you ask something',
        'Writing the answer',
        'Limited to the moment the request is answered',
      ],
      [...CRASHLYTICS_OVERSEAS_ROW],
      [...REVENUECAT_OVERSEAS_ROW],
      [
        'Google LLC',
        'The United States and other countries where Google operates',
        'Account identifier, name, and email address',
        'Sent over HTTPS at the moment you choose to sign in with Google',
        'Authenticating a Google account sign-in',
        'Limited to the moment authentication is processed',
      ],
      [
        'Apple Inc.',
        'The United States',
        'Account identifier and email address',
        'Sent over HTTPS at the moment you choose Sign in with Apple',
        'Authenticating an Apple account sign-in',
        'Limited to the moment authentication is processed',
      ],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'In the app — a conversation can be deleted directly, and your birth details and display name can be changed at any time (changing them rebuilds the chart)',
      'Deleting your account — withdrawing from “My account” in the app destroys your birth details, your chart, your conversations, and your answer ledger immediately',
      'Reading your own chart without an account — the website shows the chart for any birth details you type, calculated in your browser and stored nowhere',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'your birth details, the chart derived from them, your conversations, and the text of support messages',
  ),
  noAutomaticCollection(
    '**The service currently shows no advertising**, and no advertising identifier is collected. The only purchase it offers is a subscription that removes the limit on how many answers you may ask for; payment is carried out by the Apple App Store or Google Play, so the Operator never sees your card details, and a subscription is cancelled in the store’s own subscription settings, which the app can open for you but cannot act on itself. The Operator processes only the purchase record described in Articles 2, 3, 6, and 7. Should advertising be introduced, this policy will be updated before it goes live.',
  ),
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 28, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
  ]),
]

export const KKIMSAE_POLICY: PolicyDocument = {
  slug: 'kkimsae',
  title: '낌새 (Kkimsae) Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for 낌새 (Kkimsae, “the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
